
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CheckCircle, XCircle, Clock, User, Calendar, Search } from 'lucide-react';
import { format } from 'date-fns';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { useToast } from '@/hooks/use-toast';

interface AdminLeaveRequestsProps {
  statusFilter?: 'all' | 'pending' | 'approved' | 'rejected';
}

interface LeaveRequest {
  id: string;
  user_id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  reason: string;
  status: string;
  created_at: string;
  profiles: {
    full_name: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  teams: Array<{
    id: string;
    name: string;
  }>;
}

export const AdminLeaveRequests: React.FC<AdminLeaveRequestsProps> = ({ 
  statusFilter = 'all' 
}) => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: requests, isLoading, refetch } = useQuery({
    queryKey: ['admin-leave-requests', statusFilter],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      // Check if user is super admin
      const { data: isSuperAdmin } = await supabase.rpc('is_super_admin', { user_id: user.id });

      let allowedUserIds: string[] = [];

      if (isSuperAdmin) {
        // Super admins see all requests - no filtering needed
        allowedUserIds = [];
      } else {
        // Get admin group (parent + contributors)
        const { data: adminGroup } = await supabase.rpc('get_admin_group', { user_id: user.id });

        if (!adminGroup || adminGroup.length === 0) {
          return [];
        }

        const adminGroupIds = adminGroup.map((g: any) => g.admin_id);

        // Get all users created by anyone in the admin group
        const { data: groupUsers } = await supabase
          .from('profiles')
          .select('id')
          .in('created_by', adminGroupIds);

        if (!groupUsers || groupUsers.length === 0) {
          return [];
        }

        allowedUserIds = groupUsers.map(u => u.id);
      }

      // First get leave requests filtered by user access
      let query = supabase
        .from('leave_requests')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by allowed users if not super admin
      if (!isSuperAdmin && allowedUserIds.length > 0) {
        query = query.in('user_id', allowedUserIds);
      }

      // Apply status filter
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data: leaveRequests, error: leaveError } = await query;
      if (leaveError) throw leaveError;

      if (!leaveRequests || leaveRequests.length === 0) {
        return [];
      }

      console.log('Leave requests fetched:', leaveRequests);

      // Get user IDs to fetch profiles
      const userIds = leaveRequests.map(req => req.user_id);
      console.log('User IDs:', userIds);
      
      // Fetch profiles separately
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, email')
        .in('id', userIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      console.log('Profiles fetched:', profiles);

      // Fetch team memberships for each user
      const { data: teamMembers, error: teamMembersError } = await supabase
        .from('team_members')
        .select('profile_id, team_id, teams(id, name)')
        .in('profile_id', userIds);

      if (teamMembersError) {
        console.error('Error fetching team members:', teamMembersError);
      }

      console.log('Team members fetched:', teamMembers);

      // Combine leave requests with profiles and teams
      const requestsWithProfilesAndTeams = leaveRequests.map(request => {
        const profile = profiles?.find(profile => profile.id === request.user_id) || null;
        const userTeams = teamMembers?.filter(tm => tm.profile_id === request.user_id) || [];
        
        console.log(`User ${request.user_id} teams:`, userTeams);
        
        return {
          ...request,
          profiles: profile,
          teams: userTeams.map(tm => ({
            id: tm.team_id,
            name: tm.teams?.name || 'Unknown Team'
          }))
        };
      });

      console.log('Final requests with teams:', requestsWithProfilesAndTeams);
      return requestsWithProfilesAndTeams as LeaveRequest[];
    }
  });

  // Filter requests based on search term
  const filteredRequests = useMemo(() => {
    if (!requests || !searchTerm.trim()) {
      return requests || [];
    }

    const searchLower = searchTerm.toLowerCase();
    
    return requests.filter(request => {
      // Search in user's name
      const fullName = request.profiles?.full_name?.toLowerCase() || '';
      const firstName = request.profiles?.first_name?.toLowerCase() || '';
      const lastName = request.profiles?.last_name?.toLowerCase() || '';
      const email = request.profiles?.email?.toLowerCase() || '';
      
      // Search in team names
      const teamNames = request.teams.map(team => team.name.toLowerCase()).join(' ');
      
      return (
        fullName.includes(searchLower) ||
        firstName.includes(searchLower) ||
        lastName.includes(searchLower) ||
        email.includes(searchLower) ||
        teamNames.includes(searchLower)
      );
    });
  }, [requests, searchTerm]);

  const updateRequestStatus = async (requestId: string, newStatus: string) => {
    try {
      // Find the request details
      const request = requests?.find(r => r.id === requestId);
      
      const { error } = await supabase
        .from('leave_requests')
        .update({ status: newStatus })
        .eq('id', requestId);

      if (error) throw error;

      // Send notification email if approved
      if (newStatus === 'approved' && request) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          
          if (session) {
            await supabase.functions.invoke('send-approval-notification', {
              body: {
                type: 'leave',
                requestId: request.id,
                userName: request.profiles?.full_name || 'Unknown',
                userEmail: request.profiles?.email || '',
                leaveType: request.leave_type,
                startDate: request.start_date,
                endDate: request.end_date,
                reason: request.reason,
                teamIds: request.teams.map(t => t.id),
              },
            });
            console.log('Notification email sent for leave approval');
          }
        } catch (emailError) {
          console.error('Failed to send notification email:', emailError);
          // Don't block the approval if email fails
        }
      }

      toast({
        title: 'Success',
        description: `Leave request ${newStatus} successfully`,
      });

      refetch();
    } catch (error) {
      console.error('Error updating leave request:', error);
      toast({
        title: 'Error',
        description: 'Failed to update leave request',
        variant: 'destructive',
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'REPO':
        return <Badge className="bg-red-100 text-red-700 border-red-200">REPO</Badge>;
      case 'LEAVE':
        return <Badge className="bg-green-100 text-green-700 border-green-200">LEAVE</Badge>;
      case 'OTHER':
        return <Badge className="bg-orange-100 text-orange-700 border-orange-200">OTHER</Badge>;
      default:
        return <Badge variant="outline">{type}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search Filter */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
        <Input
          type="text"
          placeholder="Search by name, last name, or team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2"
        />
      </div>

      {!filteredRequests?.length ? (
        <div className="text-center py-8 text-muted-foreground">
          <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{searchTerm ? 'No requests found matching your search' : 'No leave requests found'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests.map((request) => {
            console.log('Rendering request:', request.id, 'with teams:', request.teams);
            return (
              <Card key={request.id} className="p-4 overflow-hidden bg-card border-border">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-foreground truncate">
                          {request.profiles?.full_name || 
                           `${request.profiles?.first_name || ''} ${request.profiles?.last_name || ''}`.trim() || 
                           request.profiles?.email || 
                           'Unknown User'}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {getTypeBadge(request.leave_type)}
                          {request.teams && request.teams.length > 0 ? (
                            request.teams.map((team) => (
                              <Badge
                                key={team.id}
                                variant="outline"
                                className="bg-primary/5 border-primary/20 text-primary text-xs"
                              >
                                <span className="truncate max-w-20">{team.name}</span>
                              </Badge>
                            ))
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              No Team
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3 space-y-2">
                      <p className="text-sm text-muted-foreground">
                        <strong className="text-foreground">Period:</strong> {format(fromLocalISOString(request.start_date), 'MMM d, yyyy')} - {format(fromLocalISOString(request.end_date), 'MMM d, yyyy')}
                      </p>
                      {request.reason && (
                        <p className="text-sm text-muted-foreground">
                          <strong className="text-foreground">Reason:</strong> 
                          <span className="block mt-1 break-words text-foreground">{request.reason}</span>
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Calendar className="w-3 h-3 flex-shrink-0" />
                      <span>Submitted {format(new Date(request.created_at), 'MMM d, yyyy')}</span>
                    </div>
                  </div>
                  
                  <div className="flex flex-col gap-3 lg:items-end">
                    <div className="self-start lg:self-end">
                      {getStatusBadge(request.status)}
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      {request.status !== 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateRequestStatus(request.id, 'approved')}
                          className="text-green-600 border-green-200 hover:bg-green-50 text-xs px-2 py-1"
                        >
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Approve
                        </Button>
                      )}
                      {request.status !== 'rejected' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateRequestStatus(request.id, 'rejected')}
                          className="text-red-600 border-red-200 hover:bg-red-50 text-xs px-2 py-1"
                        >
                          <XCircle className="w-3 h-3 mr-1" />
                          Reject
                        </Button>
                      )}
                      {request.status !== 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => updateRequestStatus(request.id, 'pending')}
                          className="text-yellow-600 border-yellow-200 hover:bg-yellow-50 text-xs px-2 py-1"
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Reset
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
